import { openDB, deleteDB, IDBPDatabase } from 'idb'
import _debug from 'debug'
import { t } from 'ttag'

import { VideoCacheRecord, VideoBlob } from './VideoCacheRecord'
import { VideoCacheUploader } from './VideoCacheUploader'
import { VideoCacheDownloader, IVideoDownloadQuery } from './VideoCacheDownloader'
import { _LevelupDB } from './_LevelupDB'
import { systemError } from '../components/utils/Errors'
import { isSlttAppStorageEnabled } from './SlttAppStorage'
import { delay } from '../components/utils/delay'
import { syncLocalTeamStorageBlobs } from './VideoCache.lan'
import { convertFromCanonicalTimestamp } from './utils/dateServices'
import { once } from 'underscore'
import { CLEAR_VIDEO_CACHE, CLEAR_VIDEO_CACHE_INSTRUCTIONS_1, CLEAR_VIDEO_CACHE_INSTRUCTIONS_2, computeShouldDisableImplicitDownloads, getDbSaveErrorDetectedAt } from './VideoCache.utils'

const log = _debug('sltt:VideoCache')

const warnOnceDisableImplicitLocalStorage = once((msg: string) => console.warn(msg))
const warnOnceDisableImplicitQuota = once((msg: string) => console.warn(msg))
const warnOnceDisableImplicitSafeVcrsLessthanBytesToFree = once((msg: string) => console.warn(msg))
const warnOnceDisableImplicitEncounteredSaveToDBError = once((msg: string) => console.warn(msg))
const warnOnceDisableImplicitDuplicateDownload = once((msg: string) => console.warn(msg))

export const BLOB_BLOCK_SIZE = 4 * 1024 * 1024 // 4MB`

/** 
 * VideoCache acts as a singleton cache to store all local videos.
 * This includes videos that have been downloaded from S3
 * and videos waiting to be uploaded to S3.
 */
export class VideoCache {
    static VIDEOBLOBS = 'videoBlobs'
    static CACHEDVIDEOS = 'cachedVideos'

    static capacity = 50   // max capacity in Gig
    static _db:IDBPDatabase<unknown>
    static videoCacheUploader = new VideoCacheUploader(systemError)
    static videoCacheDownloader = new VideoCacheDownloader(systemError)
    /** projects this user has uploadAccess to */
    static projectsWithUploadAccess: string[] = []
    /** flag for disabling implicit downloads in context of storage related issues 
     * or localStorage.disableImplicit */
    static disableImplicit: boolean | null = null
    /** in devtools console set VideoCache.simulateSaveToDBError = 'IDBDatabase' to throw its message in context of vcr.saveToDB() */
    static simulateSaveToDBError: string = '' // error message to throw

    static async openDatabase() {
        VideoCache._db = await openDB('VideoCache', 1, {
            upgrade(db) {
                db.createObjectStore(VideoCache.VIDEOBLOBS, {
                    keyPath: '_id',
                    autoIncrement: true,
                })
                db.createObjectStore(VideoCache.CACHEDVIDEOS, {
                    keyPath: '_id',
                    autoIncrement: true,
                })
            },
        })
    }

    /**
     * Called every time a different project is selected
     */
    static async initialize(projectsWithUploadAccess: string[]) {
        VideoCache.projectsWithUploadAccess = projectsWithUploadAccess
        
        if (!VideoCache._db) {
            // Here on first call to this function.
            await VideoCache.openDatabase() // sets VideoCache._db
            await VideoCache.checkForCacheResetRequest()
        }

        await VideoCache.syncCache(); // semi colon reqd here so that statement is parsed correctly

        (window as any).VideoCache = VideoCache

        //await VideoCache.deleteAll()
    }

    /**
     * Get vcrs from indexeddb and update cache to match.
     * When isSlttAppStorageEnabled() sync blobs to/from disk and vcr upload status
     */
    private static async syncCache() {
        const vcrs = await VideoCache.getAllVcrsFromDB()
        for (let vcr of vcrs) {
            // ensure that cache size is set, it should have already been
            if (vcr.size === 0) {
                await vcr.setSize()
            }
            VideoCacheRecord.updateCache(vcr)
        }

        if (isSlttAppStorageEnabled()) {
            await syncLocalTeamStorageBlobs(VideoCache.projectsWithUploadAccess, vcrs)
        }
    }

    static async checkForCacheResetRequest() {
        const request = localStorage.getItem('cacheResetRequest')
        if (request !== 'YES' && request !== 'ALL') return

        localStorage.setItem('cacheResetRequest', '')

        try {
            const { vcrsUnsafeToDelete, vcrsSafeToDelete } = await VideoCache.videoCacheDownloader.getSafeAndUnsafeVcrsToDelete(
                { treatRecentAccessDateAsSafe: true }
            )
            const cachedUnsafeToDelete = vcrsUnsafeToDelete.filter(vcr => vcr.hasBeenCached)
            if (cachedUnsafeToDelete.length === 0  || request === 'ALL') {
                const ok = await VideoCache.attemptDeleteDBWithTimeout('VideoCache', 1000)
                if (!ok) {
                    alert(t`Could not clear video cache because another browser window is using the database.` + '\n'
                        + CLEAR_VIDEO_CACHE_INSTRUCTIONS_1 + '\n'
                        + CLEAR_VIDEO_CACHE_INSTRUCTIONS_2 + '.\n'
                        + t`Then try again: Settings > Debug >` + ' ' + CLEAR_VIDEO_CACHE)
                    window.location.reload()
                    return
                }

                alert(t`VideoCache reset DONE`)
                await VideoCache.openDatabase()
            } else {
                const uncachedSafeToDelete = vcrsUnsafeToDelete.filter(vcr => !vcr.hasBeenCached)
                for (const vcrToDelete of [...vcrsSafeToDelete, ...uncachedSafeToDelete]) {
                    await vcrToDelete.delete()
                }
                alert(`VideoCache reset DONE.\n\n${cachedUnsafeToDelete.length} videos were not deleted because they are still in use.`)
            }
        } 
        catch(error) {
            alert('VideoCache reset FAILED')
        }
    }

    // User this when user has explicitly requeted to display a video.
    // It will be scheduled to download with high priority.
    // You can call this repeated with a short delay to get updated information
    // about the download status of the video ... and when the download completes
    // a blob containing the video.
    static async queryVideoDownload(videoUrl: string): Promise<IVideoDownloadQuery> {
        const result = await VideoCache.videoCacheDownloader.queryVideoDownload(videoUrl)
        return result
    }

    static async getVideoDownload(videoUrl: string): Promise<Blob> {
        log('getVideoDownload', videoUrl)

        while (true) {
            let result = await this.videoCacheDownloader.queryVideoDownload(videoUrl)
            if (result.blob) return result.blob
            log(result.message)
            await delay(1000)
        }
    }

    // Return a sorted list of scheduled downloads for debugging purposes
    static queryScheduledDownloads() {
        return this.videoCacheDownloader.queryScheduledDownloads()
    }

    static async queryProgress(videoUrl: string) {
        return await this.videoCacheDownloader.getProgress(videoUrl)
    }

    /** Use this when the user has done something that makes it reasonably likely
    * they are going to want to access this video in the near future.
    * Video will be scheduled for downloading but at a low priority.
    * SIDE-EFFECT: VideoCache.disableImplicit will be set false if 
    * computeShouldDisableImplicitDownloads() returns true
    * Returns true if we know that the requested video has already been downloaded.
    */ 
    static async implicitVideoDownload(videoUrl: string) {
        const origDisableImplicit = VideoCache.disableImplicit
        if ((await computeShouldDisableImplicitDownloads({
            getSafeToDeleteVcrs: VideoCache.videoCacheDownloader.getSafeToDeleteVcrs,
            dbSaveErrorDetectedAt: getDbSaveErrorDetectedAt(),
            duplicateDownloadDetectedAt: VideoCache.videoCacheDownloader.duplicateDownloadTimestamp,
            warnOnceDisableImplicitLocalStorage,
            warnOnceDisableImplicitQuota,
            warnOnceDisableImplicitSafeVcrsLessthanBytesToFree,
            warnOnceDisableImplicitEncounteredSaveToDBError,
            warnOnceDisableImplicitDuplicateDownload,
        }))) {
            VideoCache.disableImplicit = true
            if (!origDisableImplicit) {
                console.warn('clearing scheduledDownloads to apply disableImplicit')
                // clear any currently scheduled downloads
                // so we can begin to apply disableImplicit immediately
                this.videoCacheDownloader.clearImplicitDownloads()
            }
            return false
        }

        return await this.videoCacheDownloader.implicitVideoDownload(videoUrl)
    }

    /**
     * Return status of upload for this video.
     * Empty string means video did not need uploading or uploading complete.
     */
    static async queryVideoUpload(_id: string): Promise<string> {
        let vcr = await VideoCacheRecord.get(_id)
        
        let { uploadeds, error } = vcr

        if (uploadeds.length === 0) return ''
        
        let totalUploaded = uploadeds.filter(Boolean).length
        if (totalUploaded === uploadeds.length) return ''

        if (error) return error

        let message = `Uploaded ${totalUploaded} of ${uploadeds.length} ...`
        if (totalUploaded === 0) message = 'Waiting to upload ...'

        return message
    }

    static async accept(_id: string, creationDate: string) {
        let vcr = await VideoCacheRecord.get(_id)
        
        // Every -id should end in -n to tell us how many S3 items
        // for this video, if not we cannot download ig
        let seqNum = vcr.seqNum(_id)
        if (seqNum <= 0) {
            await vcr.setInvalidSeqNumError(_id)
            return
        }

        if (vcr.uploadeds.length) {
            if (!vcr.uploaded) {
                VideoCache.videoCacheUploader.postMessage({ func: 'upload', _id: vcr._id })
            }
            return
        }

        // If this video was created recently but not downloaded yet,
        // schedule it to be downloaded at a low priority.
        if (!vcr.downloaded) {
            let dt = new Date()
            dt.setDate(dt.getDate() - 7) // 7 days before today
            if (convertFromCanonicalTimestamp(creationDate) >= dt) {
                VideoCache.implicitVideoDownload(_id)
            }

            return
        }
    }

    /**
     * @return {true} iff there is a record present in the cache for this id.
     *    WARNING the precense of a cache record does not mean that the item
     *    has finished uploading or downloding.
     */
    static async isPresent(_id: string) {
        let vcr = await VideoCacheRecord.get(_id)
        return vcr
    }

    // If this video has already been downloaded, return it as a blob.
    // Otherwise return null.
    static async getVideoBlob(_id: string): Promise<Blob | null> {
        log(`getVideoBlob ${_id}`)
        let vcr = await VideoCacheRecord.get(_id)

        // Update the access date. Least recently used unlocked blobs are
        // deleted when necessary to make room for more recently accessed blobs.
        await vcr.touch()

        if(!vcr.hasBeenCached) return null

        return await vcr.getVideoBlob()
    }

    /*
     *    This is useful from the console to download a video you want to inspect.
     *    For example to get the video for the 2nd video in the currently selected passage
     * 
     *        let url = _.rt.passage.videos[1].url
     *        window.VideoCache.downloadVideoBlob(url).catch(console.log)
     */
    static async downloadVideoBlob(_id: string) {
        log(`downloadVideoBlob ${_id}`)
        let vcr = await VideoCacheRecord.get(_id)
        if (!vcr.hasBeenCached) {
            log('not found in cache')
            return
        }

        let blob = await vcr.getVideoBlob()

        let href = window.URL.createObjectURL(blob!)
        let link = document.createElement('a')
        link.setAttribute('href', href)
        link.setAttribute('download', `temp.mp4`)
        link.click()
    }

    /**
     * Request video. Move it to front of download queue if it has not already been downloaded.
     * @return - true iff already downloaded.
     */ 
    // static async requestVideo(_id: string): Promise<boolean> {
    //     log(`requestVideoDownload ${_id}`)

    //     let vcr = new VideoCacheRecord({ _id })
    //     await vcr.loadFromDB('VideoCache requestVideo')
    //     if (vcr.uploadeds.length === 0) {
    //         await vcr.setupDownload()
    //     }

    //     if (vcr.hasBeenCached) {
    //         return true // video already present in cache
    //     }

    //     return false
    // }

    static async copyFileToVideoCache(file: Blob, baseUrl: string, creationDate?: string, requestUpload?: boolean) {
        if (!creationDate) {
            creationDate = _LevelupDB.getDate()
        }

        let uploadSingleFile = false
        if (uploadSingleFile) {
            return await VideoCache.copySingleFileToVideoCache(file, baseUrl, creationDate, requestUpload)
        } else {
            return await VideoCache.copyMultipartFileToVideoCache(file, baseUrl, creationDate, requestUpload)
        }
    }

    private static async copySingleFileToVideoCache(file: Blob, baseUrl: string, creationDate?: string, requestUpload?: boolean) {
        let _id = `${baseUrl}-1`
        log('copyFileToVideoCache', requestUpload, file.size, _id)

        let vcr = await VideoCacheRecord.get(_id)

        // Project images may have been previously uploaded. Start over.
        // Other uploads contain unique names so the following line does nothing.
        vcr.uploadeds = []

        vcr.size = file.size
        await vcr.addBlob(file)

        let uploadSuccess = await VideoCache.didUploadSucceed(vcr._id)
        if (!uploadSuccess) {
            throw Error(t`Could not read file. If copying from Dropbox or Google Drive try making a local copy first.`)
        }

        // For videos and images which are referenced in Project the upload is requested
        // when the referencing DB record is accepted.
        // For project images however there is no DB record in the local DB.
        // For those entries we set requestUpload to trigger the upload.
        if (requestUpload) {
            VideoCache.videoCacheUploader.postMessage({ func: 'upload', _id: vcr._id })
        }

        log('copyFileToVideoCache DONE')
        return _id
    }

    private static async copyMultipartFileToVideoCache(file: Blob, baseUrl: string, creationDate?: string, requestUpload?: boolean) {
        let blobsCount = Math.floor(((file.size-1) / BLOB_BLOCK_SIZE) + 1)
        let _id = `${baseUrl}-${blobsCount}`
        log('copyFileToVideoCache', requestUpload, file.size, _id)

        let offset = 0
        let seqNum = 1
        let vcr = await VideoCacheRecord.get(_id)

        // Project images may have been previously uploaded. Start over.
        // Other uploads contain unique names so the following line does nothing.
        vcr.uploadeds = []

        vcr.size = file.size

        const userMessageGoogleDriveOrDropbox = t`Could not read file. If copying from Dropbox or Google Drive try making a local copy first.`

        while (offset < file.size) {
            let endOffset = offset + BLOB_BLOCK_SIZE
            if (endOffset > file.size) endOffset = file.size

            let blob = file.slice(offset, endOffset, file.type)
            // TODO: possibly regenerate the blob here instead inside VideoBlob.saveToDB()
            try {
                await vcr.addBlob(blob)
            } catch (error) {
                throw Error(userMessageGoogleDriveOrDropbox)
            }

            offset += BLOB_BLOCK_SIZE
            seqNum = seqNum + 1
        }

        await vcr.saveToDB()

        let uploadSuccess = await VideoCache.didUploadSucceed(vcr._id)
        if (!uploadSuccess) {
            throw Error(userMessageGoogleDriveOrDropbox)
        }

        // For videos and images which are referenced in Project the upload is requested
        // when the referencing DB record is accepted.
        // For project images however there is no DB record in the local DB.
        // For those entries we set requestUpload to trigger the upload.
        if (requestUpload) {
            VideoCache.videoCacheUploader.postMessage({ func: 'upload', _id: vcr._id })
        }

        log('copyFileToVideoCache DONE')
        return _id
    }

    // Verify all blobs for a video cache record are actually stored in the cache.
    // Sometimes copying from dropbox does odd things that we need to catch.
    private static async didUploadSucceed(_id: string) {
        let vcr = await VideoCacheRecord.get(_id)
        for (let i=0; i<vcr.uploadeds.length; ++i) {
            try {
                let blobId = vcr.videoBlobId(i)
                let videoBlob = new VideoBlob(blobId)
                await videoBlob.loadFromDB(true)
            } catch (error) {
                return false
            }
        }

        return true
    }

    /** Reads all vcrs from indexeddb, does not read or write VideoCacheRecord.cache */
    static async getAllVcrsFromDB() {
        const docs = await VideoCache._db.getAll(VideoCache.CACHEDVIDEOS)
        const vcrs = docs.map(doc => new VideoCacheRecord(doc))
        return vcrs
    }

    // UTILITY methods for unit tests
    
    static async getAllVideoBlobs() {
        let docs = await VideoCache._db.getAll(VideoCache.VIDEOBLOBS)
        return docs
    }

    static async getAllVideoBlobIds() {
        const keys = await VideoCache._db.getAllKeys(VideoCache.VIDEOBLOBS)
        return keys.map(key => key.toString())
    }

    static async getRecordsForKeys(keys: string[]): Promise<{ _id: string, blob: Blob }[]> {
        const tx = VideoCache._db.transaction(VideoCache.VIDEOBLOBS, 'readonly')
        const store = tx.objectStore(VideoCache.VIDEOBLOBS)
        const promises = keys.map(key => store.get(key))
        const records = await Promise.all(promises)
        await tx.done
        return records
    }

    static dump(match?: string) {
        VideoCache.getAllVcrsFromDB()
            .then(vcrs => {
                for (let vcr of vcrs) {
                    if (match && !vcr._id.includes(match)) continue
                    console.log(JSON.stringify(vcr,null,4))
                }

                return VideoCache.getAllVideoBlobs()
            })
            .then(vbs => {
                for (let vb of vbs) {
                    if (match && !vb._id.includes(match)) continue
                    console.log(vb._id, vb.blob ? '' : '!!!')
                }
            })
    }

    static async deleteAll() {
        let vcrs = await VideoCache.getAllVcrsFromDB()
        for (let vcr of vcrs) {
            log(`delete vcr ${vcr._id}`)
            await VideoCache._db.delete(VideoCache.CACHEDVIDEOS, vcr._id)
        }

        let vbs = await VideoCache.getAllVideoBlobs()
        for (let vb of vbs) {
            log(`delete VideoBlob ${vb._id}`)
            await VideoCache._db.delete(VideoCache.VIDEOBLOBS, vb._id)
        }
    }

    static async deleteCache() {
        try {
            await deleteDB(VideoCache.CACHEDVIDEOS)
        } catch (err) {
            log('deletecache error', err)
        }

        try {
            await deleteDB(VideoCache.VIDEOBLOBS)
        } catch (err) {
            log('deletecache error', err)
        }
    }

    static stressTest() {
        VideoCache.videoCacheDownloader.stressTest()
            .catch(err => log(err))
    }

    // Attempt to delete an IndexedDB database but bail out if it takes longer than timeoutMs.
    // Returns true if delete succeeded, false on timeout or error (so caller can show friendly message).
    private static async attemptDeleteDBWithTimeout(dbName: string, timeoutMs = 1000): Promise<boolean> {
        try {
            // Attempt to close our handle first (await), then delete the DB, but bail out if the
            // combined operation takes longer than timeoutMs.
            const closeThenDelete = (async () => {
                if (VideoCache._db) {
                    try {
                        // some browsers might hang on close(); await in case close() returns a promise
                        // (idb's DB.close() is synchronous but underlying implementations vary).
                        (VideoCache._db as any).close()
                    } catch (err) {
                        // if close() throws synchronously, log and continue to delete attempt
                        log('error closing _db before delete', err)
                    }
                }
                // after close attempt, try deleting the DB
                return await deleteDB(dbName)
            })()

            await Promise.race([
                closeThenDelete,
                new Promise((_res, rej) => setTimeout(() => rej(new Error('delete-timeout')), timeoutMs))
            ])
            return true
        } catch (err) {
            log('deleteDB failed or timed out', err)
            return false
        }
    }

    static stopStressTest = false

    static async testRepeatedDownload(rt: { passageVideo: { url: string } | null }) {
        const { passageVideo } = rt
        if (!passageVideo) {
            console.log('testRepeatedDownload: no passageVideo selected')
            return
        }
        const { url } = passageVideo
        for (let i = 0; i < 2; i++) {
            const vcr = await VideoCacheRecord.get(url)
            await vcr.deleteBlobs({ treatRecentAccessDateAsSafe: true })
            await VideoCache.getVideoDownload(url)
        }
        
        await VideoCache.implicitVideoDownload(url)
        log(VideoCache.disableImplicit ? 'Success' : 'Failed to set disabledImplicit')
    }
}

const _window = window as any
_window.videoCacheStressTest = VideoCache.stressTest
_window.VideoCache = VideoCache

// console.log(await window.VideoCache.videoCacheDownloader.getProgress('TESTnm/200829_201205/210519_194355/210519_194356-9'))